import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  Fade,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  Card,
  CardContent,
  Grid,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Button
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Book as BookIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Quiz as QuizIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Help as HelpIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Slideshow as PptIcon,
  VideoFile as VideoIcon,
  InsertDriveFile as FileIcon,
  CloudUpload as CloudUploadIcon,
  EmojiEvents as EmojiEventsIcon,
  ThumbUp as ThumbUpIcon
} from '@mui/icons-material';
import CoursService from '../../services/CoursService';
import MatiereService from '../../services/MatiereService';
import MainContainer from '../../components/MainContainer';
import { headerBanqueCV } from '../../data/modules/banque-cv-header';

const Cours = () => {
  const [cours, setCours] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [matiereFilter, setMatiereFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [qcmModalOpen, setQcmModalOpen] = useState(false);
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [editingCours, setEditingCours] = useState(null);
  const [currentCours, setCurrentCours] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Dialogue de feedback
  const [feedbackDialog, setFeedbackDialog] = useState({
    open: false,
    type: 'success',
    title: '',
    message: '',
    onConfirm: null
  });
  
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    matiere_id: '',
    file: null,
    qcm_type: 1,
    prerequisite_course_id: '',
    min_prereq_score: 50,
    order_index: 0
  });
  
  // Fonction pour parser les données JSON d'une question
  const parseQuestionData = (question) => {
    if (!question) return null;
    
    let parsedOptions = [];
    let parsedCorrectAnswers = [];
    
    // Parser les options
    if (question.options) {
      if (typeof question.options === 'string') {
        try {
          parsedOptions = JSON.parse(question.options);
        } catch (e) {
          console.error('Erreur parsing options:', e);
          parsedOptions = [];
        }
      } else if (Array.isArray(question.options)) {
        parsedOptions = question.options;
      }
    }
    
    // Parser les bonnes réponses
    if (question.correct_ans) {
      if (typeof question.correct_ans === 'string') {
        try {
          parsedCorrectAnswers = JSON.parse(question.correct_ans);
        } catch (e) {
          console.error('Erreur parsing correct_ans:', e);
          parsedCorrectAnswers = [];
        }
      } else if (Array.isArray(question.correct_ans)) {
        parsedCorrectAnswers = question.correct_ans;
      }
    }
    
    return {
      ...question,
      options: parsedOptions,
      correct_ans: parsedCorrectAnswers,
      points: parseInt(question.points) || 1,
      timer_seconds: parseInt(question.timer_seconds) || 0
    };
  };

  // État initial pour le formulaire de question
  const getInitialQuestionForm = () => ({
    question: '',
    type: 'unique',
    options: ['', ''],
    correct_ans: [],
    points: 1,
    timer_seconds: 0
  });

  const [questionForm, setQuestionForm] = useState(getInitialQuestionForm());

  // Définir getMatiereName AVANT de l'utiliser dans le filtre
  const getMatiereName = (matiereId) => {
    const matiere = matieres.find(m => parseInt(m.id) === parseInt(matiereId));
    return matiere ? matiere.nom : '-';
  };

  useEffect(() => {
    loadCours();
    loadMatieres();
  }, []);

  const loadCours = async () => {
    setLoading(true);
    try {
      const result = await CoursService.getAllCours();
      if (result && result.status === true) {
        setCours(result.data || []);
      } else {
        setCours([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des cours:', error);
      setCours([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMatieres = async () => {
    try {
      const result = await MatiereService.getAllMatieres();
      if (result && result.status === true) {
        setMatieres(result.data || []);
      } else {
        setMatieres([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des matières:', error);
      setMatieres([]);
    }
  };

  const loadQuestions = async (courseId) => {
    try {
      const result = await CoursService.getQuestionsByCourse(courseId);
      if (result && result.status === true) {
        // Parser chaque question pour décoder les JSON
        const validQuestions = (result.data || [])
          .filter(q => q && typeof q === 'object')
          .map(q => parseQuestionData(q));
        setQuestions(validQuestions);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des questions:', error);
      setQuestions([]);
    }
  };

  const showFeedback = (type, title, message, onConfirm = null) => {
    setFeedbackDialog({
      open: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeFeedback = () => {
    setFeedbackDialog({ ...feedbackDialog, open: false });
    if (feedbackDialog.onConfirm) {
      feedbackDialog.onConfirm();
    }
  };

  const getFeedbackIcon = (type) => {
    switch (type) {
      case 'success':
        return <EmojiEventsIcon sx={{ fontSize: 80, color: '#4caf50' }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 80, color: '#f44336' }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 80, color: '#ff9800' }} />;
      default:
        return <ThumbUpIcon sx={{ fontSize: 80, color: '#4caf50' }} />;
    }
  };

  const getFeedbackColor = (type) => {
    switch (type) {
      case 'success':
        return '#4caf50';
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      default:
        return '#4caf50';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isValidType = file.name.match(/\.(pdf|ppt|pptx|doc|docx|mp4|avi|mpeg|mov|mkv|wmv)$/i);
      
      if (!isValidType) {
        showFeedback('error', 'Format non supporté', 'Formats acceptés: PDF, PPT, DOC, MP4, AVI, MPEG');
        return;
      }
      
      const maxSize = file.type.startsWith('video/') ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        showFeedback('error', 'Fichier trop volumineux', `Taille maximale: ${maxSize / (1024 * 1024)}MB`);
        return;
      }
      
      setSelectedFile(file);
      setFormData({ ...formData, file: file });
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return <FileIcon />;
    const ext = fileName.split('.').pop().toLowerCase();
    const iconProps = { sx: { fontSize: 20 } };
    
    switch (ext) {
      case 'pdf':
        return <PdfIcon color="error" {...iconProps} />;
      case 'ppt':
      case 'pptx':
        return <PptIcon color="warning" {...iconProps} />;
      case 'doc':
      case 'docx':
        return <DocIcon color="primary" {...iconProps} />;
      case 'mp4':
      case 'avi':
      case 'mpeg':
      case 'mov':
      case 'mkv':
      case 'wmv':
        return <VideoIcon color="secondary" {...iconProps} />;
      default:
        return <FileIcon color="action" {...iconProps} />;
    }
  };

  const getFileTypeLabel = (fileName) => {
    if (!fileName) return '';
    const ext = fileName.split('.').pop().toLowerCase();
    const types = {
      'pdf': 'PDF',
      'ppt': 'PowerPoint',
      'pptx': 'PowerPoint',
      'doc': 'Word',
      'docx': 'Word',
      'mp4': 'Vidéo MP4',
      'avi': 'Vidéo AVI',
      'mpeg': 'Vidéo MPEG',
      'mov': 'Vidéo MOV',
      'mkv': 'Vidéo MKV',
      'wmv':'Vidéo WMV'
    };
    return types[ext] || 'Document';
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      showFeedback('error', 'Erreur de validation', 'Le titre du cours est requis');
      return;
    }
    if (!formData.matiere_id) {
      showFeedback('error', 'Erreur de validation', 'Veuillez sélectionner une matière');
      return;
    }

    setSaveLoading(true);
    setUploadProgress(0);
    
    try {
      let result;
      const dataToSend = new FormData();
      dataToSend.append('title', formData.title);
      dataToSend.append('summary', formData.summary || '');
      dataToSend.append('matiere_id', formData.matiere_id);
      dataToSend.append('qcm_type', formData.qcm_type);
      dataToSend.append('min_prereq_score', formData.min_prereq_score);
      dataToSend.append('order_index', formData.order_index);
      
      if (formData.prerequisite_course_id) {
        dataToSend.append('prerequisite_course_id', formData.prerequisite_course_id);
      }
      
      if (formData.file) {
        dataToSend.append('file', formData.file);
      }

      if (editingCours) {
        result = await CoursService.updateCours(editingCours.id, dataToSend);
      } else {
        result = await CoursService.createCours(dataToSend);
      }

      if (result && result.status === true) {
        showFeedback('success', 'Succès', result.message || (editingCours ? 'Cours mis à jour avec succès' : 'Cours créé avec succès'), () => {
          setModalOpen(false);
          setEditingCours(null);
          setSelectedFile(null);
          setFormData({
            title: '',
            summary: '',
            matiere_id: '',
            file: null,
            qcm_type: 1,
            prerequisite_course_id: '',
            min_prereq_score: 50,
            order_index: 0
          });
          loadCours();
        });
      } else {
        showFeedback('error', 'Erreur', result?.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showFeedback('error', 'Erreur', error?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaveLoading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id, title) => {
    setConfirmDialog({
      open: true,
      title: 'Confirmation de suppression',
      message: `Êtes-vous sûr de vouloir supprimer le cours "${title}" ? Toutes les questions QCM et les fichiers associés seront également supprimés. Cette action est irréversible.`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        setSaveLoading(true);
        try {
          const result = await CoursService.deleteCours(id);
          if (result && result.status === true) {
            showFeedback('success', 'Succès', result.message, () => {
              loadCours();
            });
          } else {
            showFeedback('error', 'Erreur', result?.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          showFeedback('error', 'Erreur', 'Erreur lors de la suppression');
        } finally {
          setSaveLoading(false);
        }
      }
    });
  };

  const openQcmManager = async (cours) => {
    setQuestions([]);
    setCurrentCours(cours);
    await loadQuestions(cours.id);
    setQcmModalOpen(true);
  };

  const closeQcmManager = () => {
    setQcmModalOpen(false);
    setCurrentCours(null);
    setQuestions([]);
  };

  const openQuestionModal = (question = null) => {
    if (question && question.id) {
      setEditingQuestion(question);
      
      // Utiliser les données déjà parsées
      const options = question.options && Array.isArray(question.options) && question.options.length > 0
        ? [...question.options]
        : ['', ''];
      
      const correctAnswers = question.correct_ans && Array.isArray(question.correct_ans)
        ? [...question.correct_ans]
        : [];
      
      setQuestionForm({
        question: question.question || '',
        type: question.type || 'unique',
        options: options,
        correct_ans: correctAnswers,
        points: question.points || 1,
        timer_seconds: question.timer_seconds || 0
      });
    } else {
      setEditingQuestion(null);
      setQuestionForm(getInitialQuestionForm());
    }
    setQuestionModalOpen(true);
  };

  const closeQuestionModal = () => {
    setQuestionModalOpen(false);
    setEditingQuestion(null);
    setQuestionForm(getInitialQuestionForm());
  };

  const addQuestionOption = () => {
    setQuestionForm(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const updateQuestionOption = (index, value) => {
    const newOptions = [...questionForm.options];
    newOptions[index] = value;
    setQuestionForm(prev => ({ ...prev, options: newOptions }));
  };

  const removeQuestionOption = (index) => {
    if (questionForm.options.length <= 2) {
      showFeedback('warning', 'Attention', 'Au moins 2 options sont requises');
      return;
    }
    const newOptions = questionForm.options.filter((_, i) => i !== index);
    setQuestionForm(prev => ({ ...prev, options: newOptions }));
  };

  const toggleCorrectAnswer = (optionText) => {
    if (questionForm.type === 'unique') {
      setQuestionForm(prev => ({ ...prev, correct_ans: [optionText] }));
    } else {
      setQuestionForm(prev => ({
        ...prev,
        correct_ans: prev.correct_ans.includes(optionText)
          ? prev.correct_ans.filter(a => a !== optionText)
          : [...prev.correct_ans, optionText]
      }));
    }
  };

  const saveQuestion = async () => {
    if (!questionForm.question.trim()) {
      showFeedback('error', 'Erreur de validation', 'Veuillez saisir la question');
      return;
    }
    if (questionForm.type !== 'open') {
      const validOptions = questionForm.options.filter(opt => opt.trim());
      if (validOptions.length < 2) {
        showFeedback('error', 'Erreur de validation', 'Au moins 2 options sont requises');
        return;
      }
      if (questionForm.correct_ans.length === 0) {
        showFeedback('error', 'Erreur de validation', 'Veuillez sélectionner au moins une bonne réponse');
        return;
      }
    }

    const data = {
      course_id: currentCours.id,
      question: questionForm.question,
      type: questionForm.type,
      options: questionForm.options.filter(opt => opt.trim()),
      correct_ans: questionForm.correct_ans,
      points: questionForm.points,
      timer_seconds: questionForm.timer_seconds
    };

    setSaveLoading(true);
    try {
      let result;
      if (editingQuestion) {
        result = await CoursService.updateQuestion(editingQuestion.id, data);
      } else {
        result = await CoursService.createQuestion(data);
      }

      if (result && result.status === true) {
        showFeedback('success', 'Succès', result.message, () => {
          closeQuestionModal();
          loadQuestions(currentCours.id);
        });
      } else {
        showFeedback('error', 'Erreur', result?.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showFeedback('error', 'Erreur', 'Erreur lors de l\'enregistrement');
    } finally {
      setSaveLoading(false);
    }
  };

  const deleteQuestion = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Confirmation de suppression',
      message: 'Supprimer cette question ?',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        setSaveLoading(true);
        try {
          const result = await CoursService.deleteQuestion(id);
          if (result && result.status === true) {
            showFeedback('success', 'Succès', result.message, () => {
              loadQuestions(currentCours.id);
            });
          } else {
            showFeedback('error', 'Erreur', result?.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          showFeedback('error', 'Erreur', 'Erreur lors de la suppression');
        } finally {
          setSaveLoading(false);
        }
      }
    });
  };

  // Fonction de filtrage
  const filteredCours = cours.filter(c => {
    const matchSearch = c.title?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    const matiereName = getMatiereName(c.matiere_id);
    const matchMatiere = !matiereFilter || matiereName === matiereFilter;
    return matchSearch && matchMatiere;
  });

  return (
    <MainContainer {...headerBanqueCV}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Cours & QCM
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingCours(null);
              setSelectedFile(null);
              setFormData({
                title: '',
                summary: '',
                matiere_id: '',
                file: null,
                qcm_type: 1,
                prerequisite_course_id: '',
                min_prereq_score: 50,
                order_index: 0
              });
              setModalOpen(true);
            }}
            disabled={saveLoading}
            color='primary'
          >
            Nouveau cours
          </Button>
        </Box>

        {/* Filtres */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            sx={{ flex: 1, minWidth: 200 }}
            variant="outlined"
            placeholder="Rechercher un cours..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    edge="end"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filtrer par matière</InputLabel>
            <Select
              value={matiereFilter}
              onChange={(e) => setMatiereFilter(e.target.value)}
              label="Filtrer par matière"
            >
              <MenuItem value="">Toutes les matières</MenuItem>
              {matieres.map(m => (
                <MenuItem key={m.id} value={m.nom}>{m.nom}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Statistics */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`Total: ${filteredCours.length}`} 
            color="primary" 
            variant="outlined"
          />
          {searchTerm && (
            <Chip 
              label={`Résultats pour: "${searchTerm}"`} 
              color="secondary" 
              variant="outlined"
              onDelete={() => setSearchTerm('')}
            />
          )}
          {matiereFilter && (
            <Chip 
              label={`Matière: ${matiereFilter}`} 
              color="info" 
              variant="outlined"
              onDelete={() => setMatiereFilter('')}
            />
          )}
        </Box>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Titre</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Matière</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type QCM</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Prérequis</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fichier</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 1 }}>Chargement...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredCours.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      {searchTerm || matiereFilter ? (
                        <>
                          <SearchIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                          <Typography color="textSecondary">
                            Aucun cours ne correspond aux critères
                          </Typography>
                          <Button onClick={() => {
                            setSearchTerm('');
                            setMatiereFilter('');
                          }}>
                            Effacer les filtres
                          </Button>
                        </>
                      ) : (
                        <Typography color="textSecondary">
                          Aucun cours trouvé. Cliquez sur "Nouveau cours" pour en créer un.
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCours.map((coursItem, index) => (
                  <TableRow key={coursItem.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {coursItem.title}
                      </Typography>
                      {coursItem.summary && (
                        <Typography variant="caption" color="textSecondary">
                          {coursItem.summary.substring(0, 50)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{getMatiereName(coursItem.matiere_id)}</TableCell>
                    <TableCell>
                      <Chip
                        label={coursItem.qcm_type === 1 ? 'Simple' : 'Avec prérequis'}
                        color={coursItem.qcm_type === 1 ? 'info' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {coursItem.prerequisite_course ? (
                        <Tooltip title={`Score minimum requis: ${coursItem.min_prereq_score}%`}>
                          <Chip
                            label={coursItem.prerequisite_course.title}
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {coursItem.file_name ? (
                        <Tooltip title={`${getFileTypeLabel(coursItem.file_name)}: ${coursItem.file_name}`}>
                          <Chip
                            icon={getFileIcon(coursItem.file_name)}
                            label={getFileTypeLabel(coursItem.file_name)}
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Gérer les questions QCM">
                        <IconButton
                          color="secondary"
                          onClick={() => openQcmManager(coursItem)}
                          size="small"
                          disabled={saveLoading}
                        >
                          <QuizIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Modifier le cours">
                        <IconButton
                          color="primary"
                          onClick={() => {
                            setEditingCours(coursItem);
                            setSelectedFile(null);
                            setFormData({
                              title: coursItem.title || '',
                              summary: coursItem.summary || '',
                              matiere_id: coursItem.matiere_id || '',
                              file: null,
                              qcm_type: coursItem.qcm_type || 1,
                              prerequisite_course_id: coursItem.prerequisite_course_id || '',
                              min_prereq_score: coursItem.min_prereq_score || 50,
                              order_index: coursItem.order_index || 0
                            });
                            setModalOpen(true);
                          }}
                          size="small"
                          disabled={saveLoading}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer le cours">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(coursItem.id, coursItem.title)}
                          size="small"
                          disabled={saveLoading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Modal Cours */}
        <Dialog 
          open={modalOpen} 
          onClose={() => !saveLoading && setModalOpen(false)} 
          maxWidth="md" 
          fullWidth
          disableEscapeKeyDown={saveLoading}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BookIcon color="primary" />
              <Typography variant="h6">
                {editingCours ? 'Modifier le cours' : 'Nouveau cours'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Titre du cours *"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Introduction à la Programmation Web"
                fullWidth
                required
                disabled={saveLoading}
                autoFocus
              />
              
              <TextField
                label="Description"
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                multiline
                rows={3}
                placeholder="Description du cours..."
                fullWidth
                disabled={saveLoading}
              />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Matière *</InputLabel>
                    <Select
                      value={formData.matiere_id}
                      onChange={(e) => setFormData({ ...formData, matiere_id: parseInt(e.target.value) })}
                      label="Matière *"
                      disabled={saveLoading}
                    >
                      <MenuItem value="">-- Sélectionner une matière --</MenuItem>
                      {matieres.map(m => (
                        <MenuItem key={m.id} value={m.id}>{m.nom}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Ordre d'affichage"
                    type="number"
                    value={formData.order_index}
                    onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                    min="0"
                    fullWidth
                    disabled={saveLoading}
                    helperText="Plus le nombre est petit, plus le cours apparaît en haut"
                  />
                </Grid>
              </Grid>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type de QCM</InputLabel>
                    <Select
                      value={formData.qcm_type}
                      onChange={(e) => setFormData({ ...formData, qcm_type: parseInt(e.target.value) })}
                      label="Type de QCM"
                      disabled={saveLoading}
                    >
                      <MenuItem value={1}>Simple</MenuItem>
                      <MenuItem value={2}>Avec prérequis</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <input
                      accept=".pdf,.ppt,.pptx,.doc,.docx,.mp4,.avi,.mpeg,.mov,.mkv"
                      style={{ display: 'none' }}
                      id="file-upload"
                      type="file"
                      onChange={handleFileChange}
                      disabled={saveLoading}
                    />
                    <label htmlFor="file-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        fullWidth
                        startIcon={<CloudUploadIcon />}
                        disabled={saveLoading}
                        color='primary'
                      >
                        {selectedFile ? selectedFile.name : (formData.file ? formData.file.name : 'Fichier support (PDF, PPT, DOC, MP4, etc.)')}
                      </Button>
                    </label>
                    {selectedFile && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getFileIcon(selectedFile.name)}
                        <Typography variant="caption" color="textSecondary">
                          {getFileTypeLabel(selectedFile.name)} - {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedFile(null);
                            setFormData({ ...formData, file: null });
                          }}
                          disabled={saveLoading}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    {editingCours && editingCours.file_name && !selectedFile && (
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                        Fichier actuel: {editingCours.file_name}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
              
              {formData.qcm_type === 2 && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Cours prérequis</InputLabel>
                    <Select
                      value={formData.prerequisite_course_id}
                      onChange={(e) => setFormData({ ...formData, prerequisite_course_id: e.target.value })}
                      label="Cours prérequis"
                      disabled={saveLoading}
                    >
                      <MenuItem value="">-- Aucun prérequis --</MenuItem>
                      {cours
                        .filter(c => c.id !== editingCours?.id && c.matiere_id === formData.matiere_id)
                        .map(c => (
                          <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Score minimum requis (%)"
                    type="number"
                    value={formData.min_prereq_score}
                    onChange={(e) => setFormData({ ...formData, min_prereq_score: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    fullWidth
                    disabled={saveLoading}
                  />
                </>
              )}
              
              {uploadProgress > 0 && uploadProgress < 100 && (
                <Box sx={{ width: '100%', mt: 1 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    Téléchargement: {uploadProgress}%
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setModalOpen(false)} 
              disabled={saveLoading}
              color='primary'
            >
              Annuler
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              color="primary"
              disabled={saveLoading}
              startIcon={saveLoading ? <CircularProgress size={20} /> : null}
            >
              {saveLoading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal QCM */}
        <Dialog
          open={qcmModalOpen}
          onClose={() => !saveLoading && closeQcmManager()}
          maxWidth="lg"
          fullWidth
          disableEscapeKeyDown={saveLoading}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QuizIcon color="primary" />
                <Typography variant="h6">
                  Questions QCM - {currentCours?.title || ''}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openQuestionModal()}
                disabled={saveLoading}
                size="small"
                color='primary'
              >
                Ajouter une question
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {questions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <HelpIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                  <Typography color="textSecondary" sx={{ mt: 2 }}>
                    Aucune question pour ce cours
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => openQuestionModal()}
                    sx={{ mt: 2 }}
                    color='primary'
                  >
                    Ajouter la première question
                  </Button>
                </Box>
              ) : (
                questions.map((q, idx) => (
                  <Card key={q.id || idx} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={`Q${idx + 1}`}
                            color="primary"
                            size="small"
                          />
                          <Typography variant="subtitle1" fontWeight="bold">
                            {q.question || 'Question sans texte'}
                          </Typography>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => openQuestionModal(q)}
                            disabled={saveLoading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteQuestion(q.id)}
                            disabled={saveLoading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip
                          label={q.type === 'unique' ? 'Choix unique' : q.type === 'multiple' ? 'Choix multiple' : 'Réponse ouverte'}
                          color="info"
                          size="small"
                        />
                        <Chip
                          label={`${q.points || 1} pts`}
                          color="warning"
                          size="small"
                        />
                        {q.timer_seconds > 0 && (
                          <Chip
                            label={`${q.timer_seconds}s`}
                            color="error"
                            size="small"
                          />
                        )}
                      </Box>
                      {q.type !== 'open' && q.options && Array.isArray(q.options) && q.options.length > 0 && (
                        <Box sx={{ ml: 2 }}>
                          {q.options.map((opt, i) => {
                            const optionText = opt || `Option ${String.fromCharCode(65 + i)}`;
                            const isCorrect = q.correct_ans && Array.isArray(q.correct_ans) && q.correct_ans.includes(opt);
                            return (
                              <Box
                                key={i}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 1,
                                  p: 1,
                                  bgcolor: isCorrect ? '#e8f5e9' : '#fafafa',
                                  borderRadius: 1,
                                  border: isCorrect ? '1px solid #4caf50' : '1px solid #e0e0e0'
                                }}
                              >
                                <Typography variant="body2" fontWeight="bold">
                                  {String.fromCharCode(65 + i)}.
                                </Typography>
                                <Typography variant="body2">
                                  {optionText}
                                </Typography>
                                {isCorrect && (
                                  <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', ml: 'auto' }} />
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeQcmManager}  color='primary'>
              Fermer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal Question */}
        <Dialog
          open={questionModalOpen}
          onClose={() => !saveLoading && closeQuestionModal()}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown={saveLoading}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QuestionAnswerIcon color="primary" />
              <Typography variant="h6">
                {editingQuestion ? 'Modifier la question' : 'Nouvelle question'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Question *"
                value={questionForm.question}
                onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })}
                multiline
                rows={3}
                placeholder="Saisissez votre question..."
                fullWidth
                required
                disabled={saveLoading}
              />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type de réponse</InputLabel>
                    <Select
                      value={questionForm.type}
                      onChange={(e) => setQuestionForm({ ...questionForm, type: e.target.value, correct_ans: [] })}
                      label="Type de réponse"
                      disabled={saveLoading}
                    >
                      <MenuItem value="unique">Choix unique</MenuItem>
                      <MenuItem value="multiple">Choix multiple</MenuItem>
                      <MenuItem value="open">Réponse ouverte</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Points"
                    type="number"
                    value={questionForm.points}
                    onChange={(e) => setQuestionForm({ ...questionForm, points: parseInt(e.target.value) || 1 })}
                    min="1"
                    fullWidth
                    disabled={saveLoading}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Temps limite (secondes)"
                    type="number"
                    value={questionForm.timer_seconds}
                    onChange={(e) => setQuestionForm({ ...questionForm, timer_seconds: parseInt(e.target.value) || 0 })}
                    min="0"
                    fullWidth
                    disabled={saveLoading}
                    helperText="0 = illimité"
                  />
                </Grid>
              </Grid>

              {questionForm.type !== 'open' && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Options de réponse
                  </Typography>
                  {questionForm.options.map((opt, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        value={opt}
                        onChange={(e) => updateQuestionOption(idx, e.target.value)}
                        placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                        fullWidth
                        size="small"
                        disabled={saveLoading}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeQuestionOption(idx)}
                        disabled={questionForm.options.length <= 2 || saveLoading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addQuestionOption}
                    disabled={saveLoading}
                    size="small"
                    color='primary'
                  >
                    Ajouter une option
                  </Button>

                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Bonne(s) réponse(s)
                  </Typography>
                  <FormGroup>
                    {questionForm.options.filter(opt => opt.trim()).map((opt, idx) => (
                      <FormControlLabel
                        key={idx}
                        control={
                          questionForm.type === 'unique' ? (
                            <Radio
                              checked={questionForm.correct_ans.includes(opt)}
                              onChange={() => toggleCorrectAnswer(opt)}
                              disabled={saveLoading}
                            />
                          ) : (
                            <Checkbox
                              checked={questionForm.correct_ans.includes(opt)}
                              onChange={() => toggleCorrectAnswer(opt)}
                              disabled={saveLoading}
                            />
                          )
                        }
                        label={opt}
                      />
                    ))}
                  </FormGroup>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={closeQuestionModal} 
              disabled={saveLoading}
              color='primary'
            >
              Annuler
            </Button>
            <Button 
              onClick={saveQuestion} 
              variant="contained" 
              color="primary"
              disabled={saveLoading}
              startIcon={saveLoading ? <CircularProgress size={20} /> : null}
            >
              {saveLoading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Feedback Dialog */}
        <Dialog
          open={feedbackDialog.open}
          onClose={closeFeedback}
          maxWidth="xs"
          fullWidth
          TransitionComponent={Fade}
          transitionDuration={300}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={closeFeedback} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              {getFeedbackIcon(feedbackDialog.type)}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ color: getFeedbackColor(feedbackDialog.type), fontWeight: 'bold' }}
            >
              {feedbackDialog.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {feedbackDialog.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              onClick={closeFeedback}
              variant="contained"
              sx={{
                backgroundColor: getFeedbackColor(feedbackDialog.type),
                '&:hover': {
                  backgroundColor: getFeedbackColor(feedbackDialog.type),
                  opacity: 0.9
                }
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmation pour suppression */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => !saveLoading && setConfirmDialog({ ...confirmDialog, open: false })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            {confirmDialog.title}
          </DialogTitle>
          <DialogContent>
            <Typography>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
              disabled={saveLoading}
            >
              Annuler
            </Button>
            <Button 
              onClick={confirmDialog.onConfirm} 
              variant="contained" 
              color="error"
              disabled={saveLoading}
            >
              {saveLoading ? 'Suppression...' : 'Confirmer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Backdrop pour loading */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={saveLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    </MainContainer>
  );
};

export default Cours;